/**
 * TTCache provides one cache API regardless of where values are stored.
 *
 * The cache itself is responsible for:
 * - namespacing keys
 * - scoping keys (for example by session/user/company)
 * - wrapping cached values with metadata
 *
 * The storage adapter is responsible for:
 * - where the values physically live
 * - how individual items are read/written/removed
 *
 * This lets TTCurrentSession and TTPersistentSession share the same cache API
 * while storing values in different places.
 */
export class TTCache {
	storage_adapter;
	namespace;
	version;
	scope_resolver;

	constructor( options = {} ) {
		this.storage_adapter = options?.storage_adapter ?? new TTMemoryCacheAdapter();
		this.namespace = options?.namespace ?? 'TTCache';
		this.version = Number.isInteger( options?.version ) && options.version > 0 ? options.version : 1;
		this.scope_resolver = typeof options?.scope_resolver === 'function' ? options.scope_resolver : null;
	}

	isAvailable() {
		return this.storage_adapter?.isAvailable?.() !== false;
	}

	/**
	 * Resolves the current scope object from the caller-provided scope resolver.
	 * Returning null means scoped cache access is not ready yet.
	 */
	getScope() {
		if ( typeof this.scope_resolver !== 'function' ) {
			return null;
		}

		try {
			const scope = this.scope_resolver();

			if ( !scope || typeof scope !== 'object' ) {
				return null;
			}

			return scope;
		} catch ( error ) {
			return null;
		}
	}

	/**
	 * Flattens the current scope into ordered key parts.
	 * `ignore_scope` is used by cleanAll() so it can clear every scoped entry
	 * for the current namespace/version.
	 */
	getScopeParts( ignore_scope = false ) {
		if ( ignore_scope === true || typeof this.scope_resolver !== 'function' ) {
			return [];
		}

		const scope = this.getScope();

		if ( scope === null ) {
			return null;
		}

		return Object.values( scope ).map( value => String( value ) );
	}

	/**
	 * Builds the shared prefix parts for storage keys:
	 * namespace -> version -> scope -> optional group.
	 */
	getPrefixParts( group_id = null, ignore_scope = false ) {
		const scope_parts = this.getScopeParts( ignore_scope );

		if ( scope_parts === null ) {
			return null;
		}

		const prefix_parts = [this.namespace, `v${this.version}`, ...scope_parts];

		if ( group_id ) {
			prefix_parts.push( group_id );
		}

		return prefix_parts;
	}

	/**
	 * Returns the adapter prefix for a group/scope combination.
	 * Individual cache entries append their cache_id to this prefix.
	 */
	getStoragePrefix( group_id = null, ignore_scope = false ) {
		const prefix_parts = this.getPrefixParts( group_id, ignore_scope );

		if ( prefix_parts === null ) {
			return null;
		}

		return `${prefix_parts.join( ':' )}:`;
	}

	// Combines group_id and cache_id into a single logical key.
	getCacheKey( cache_id, group_id = null ) {
		if ( group_id ) {
			return `${group_id}:${cache_id}`;
		}
		return cache_id;
	}

	/**
	 * Builds the final adapter key used to store one cache item.
	 */
	getStorageKey( cache_id, group_id = null ) {
		const storage_prefix = this.getStoragePrefix( group_id );

		if ( storage_prefix === null ) {
			return null;
		}

		return `${storage_prefix}${cache_id}`;
	}

	/**
	 * Retrieves the value associated with the given cache_id and group_id.
	 */
	get( cache_id, group_id = null ) {
		if ( !this.isAvailable() ) {
			return null;
		}

		const storage_key = this.getStorageKey( cache_id, group_id );

		if ( !storage_key ) {
			return null;
		}

		return this.storage_adapter.get( storage_key )?.value ?? null;
	}

	/**
	 * Stores the data with the given cache_id and group_id.
	 */
	set( cache_id, data, group_id = null ) {
		if ( !this.isAvailable() ) {
			return false;
		}

		const storage_key = this.getStorageKey( cache_id, group_id );

		if ( !storage_key ) {
			return false;
		}

		return this.storage_adapter.set( storage_key, {
			value: data,
			created_date: Date.now(),
		} );
	}

	/**
	 * Removes the cache entry associated with the given cache_id and group_id.
	 */
	remove( cache_id, group_id = null ) {
		if ( !this.isAvailable() ) {
			return;
		}

		const storage_key = this.getStorageKey( cache_id, group_id );

		if ( !storage_key ) {
			return;
		}

		this.storage_adapter.remove( storage_key );
	}

	/**
	 * Removes all cache entries associated with the given group_id within the current scope.
	 */
	clean( group_id = null ) {
		if ( !this.isAvailable() ) {
			return;
		}

		const storage_prefix = this.getStoragePrefix( group_id );

		if ( !storage_prefix ) {
			return;
		}

		this.storage_adapter.clearPrefix( storage_prefix );
	}

	/**
	 * Removes all cache entries for this namespace across the current storage adapter.
	 */
	cleanAll() {
		if ( !this.isAvailable() ) {
			return;
		}

		const storage_prefix = this.getStoragePrefix( null, typeof this.scope_resolver === 'function' );

		if ( !storage_prefix ) {
			return;
		}

		this.storage_adapter.clearPrefix( storage_prefix );
	}
}

/**
 * In-memory storage adapter for TTCache.
 * Use this when cached values should only live for the lifetime of the page.
 */
export class TTMemoryCacheAdapter {
	cache;

	constructor() {
		this.cache = {};
	}

	isAvailable() {
		return true;
	}

	get( storage_key ) {
		return this.cache[storage_key] ?? null;
	}

	set( storage_key, item ) {
		this.cache[storage_key] = item;
		return true;
	}

	remove( storage_key ) {
		if ( this.cache[storage_key] ) {
			delete this.cache[storage_key];
		}
	}

	listKeys( prefix = '' ) {
		return Object.keys( this.cache ).filter( key => prefix === '' || key.startsWith( prefix ) );
	}

	clearPrefix( prefix = '' ) {
		if ( prefix === '' ) {
			this.cache = {};
			return;
		}

		this.listKeys( prefix ).forEach( key => {
			delete this.cache[key];
		} );
	}
}

/**
 * sessionStorage adapter for TTCache.
 * Use this when cached values should survive refreshes inside the current tab only.
 */
export class TTSessionStorageCacheAdapter {
	storage;

	constructor( storage = null ) {
		this.storage = storage;
	}

	getStorage() {
		return this.storage ?? window?.sessionStorage ?? null;
	}

	isAvailable() {
		try {
			const storage = this.getStorage();
			const test_key = '__tt_session_storage_adapter_test__';

			if ( !storage ) {
				return false;
			}

			storage.setItem( test_key, '1' );
			storage.removeItem( test_key );

			return true;
		} catch ( error ) {
			return false;
		}
	}

	get( storage_key ) {
		if ( !storage_key || !this.isAvailable() ) {
			return null;
		}

		const storage = this.getStorage();

		try {
			const raw_item = storage.getItem( storage_key );

			if ( !raw_item ) {
				return null;
			}

			return JSON.parse( raw_item );
		} catch ( error ) {
			this.remove( storage_key );
			return null;
		}
	}

	set( storage_key, item ) {
		if ( !storage_key || !this.isAvailable() ) {
			return false;
		}

		try {
			this.getStorage().setItem( storage_key, JSON.stringify( item ) );
			return true;
		} catch ( error ) {
			return false;
		}
	}

	remove( storage_key ) {
		if ( !storage_key || !this.isAvailable() ) {
			return;
		}

		this.getStorage().removeItem( storage_key );
	}

	listKeys( prefix = '' ) {
		if ( !this.isAvailable() ) {
			return [];
		}

		return Object.keys( this.getStorage() ).filter( key => prefix === '' || key.startsWith( prefix ) );
	}

	clearPrefix( prefix = '' ) {
		if ( !this.isAvailable() ) {
			return;
		}

		this.listKeys( prefix ).forEach( key => {
			this.getStorage().removeItem( key );
		} );
	}
}